import { Board } from '../../types/board';
import {
  ContractStage,
  ContractStageLinkType,
  isEmbedStageLink,
  STAGE_DOC_LINK_OPTIONS,
  STAGE_PRODUCT_LINK_OPTIONS,
  STAGE_QUALITY_LINK_OPTIONS,
  STAGE_SAFETY_LINK_OPTIONS,
} from '../../types/contract';
import './ContractStageEditor.css';

interface ContractStageEditorProps {
  stages: ContractStage[];
  boards: Board[];
  onChange: (stages: ContractStage[]) => void;
  onClose: () => void;
}

function linkSelectValue(stage: Pick<ContractStage, 'boardId' | 'linkType'>) {
  if (isEmbedStageLink(stage.linkType)) {
    return stage.linkType as string;
  }
  if (stage.boardId != null) {
    return `BOARD:${stage.boardId}`;
  }
  return '';
}

function parseLinkSelect(value: string): {
  boardId: number | null;
  linkType: ContractStageLinkType | null;
} {
  if (!value) {
    return { boardId: null, linkType: null };
  }
  if (value.startsWith('BOARD:')) {
    const id = Number(value.slice(6));
    return {
      boardId: Number.isFinite(id) ? id : null,
      linkType: Number.isFinite(id) ? 'BOARD' : null,
    };
  }
  return { boardId: null, linkType: value };
}

function ContractStageEditor({ stages, boards, onChange, onClose }: ContractStageEditorProps) {
  const writing = stages.find((s) => s.stageKey === 'WRITING') ?? {
    stageName: '작성',
    stageKey: 'WRITING' as const,
    boardId: null,
    linkType: null,
  };
  const done = stages.find((s) => s.stageKey === 'DONE') ?? {
    stageName: '완료',
    stageKey: 'DONE' as const,
    boardId: null,
    linkType: null,
  };
  const middle = stages.filter((s) => s.stageKey === 'CUSTOM');

  const commit = (
    nextMiddle: ContractStage[],
    nextDone: Pick<ContractStage, 'boardId' | 'linkType'> = {
      boardId: done.boardId ?? null,
      linkType: done.linkType ?? null,
    }
  ) => {
    onChange([
      {
        ...writing,
        stageName: '작성',
        stageKey: 'WRITING',
        boardId: null,
        linkType: null,
        sortOrder: 0,
      },
      ...nextMiddle.map((stage, index) => ({
        ...stage,
        stageKey: 'CUSTOM' as const,
        sortOrder: index + 1,
      })),
      {
        ...done,
        stageName: '완료',
        stageKey: 'DONE',
        boardId: nextDone.boardId ?? null,
        linkType: nextDone.linkType ?? null,
        sortOrder: nextMiddle.length + 1,
      },
    ]);
  };

  const updateMiddle = (index: number, patch: Partial<ContractStage>) => {
    const next = middle.map((stage, i) => (i === index ? { ...stage, ...patch } : stage));
    commit(next);
  };

  const addMiddle = () => {
    commit([
      ...middle,
      { stageName: '새 단계', stageKey: 'CUSTOM', boardId: null, linkType: null },
    ]);
  };

  const removeMiddle = (index: number) => {
    commit(middle.filter((_, i) => i !== index));
  };

  const moveMiddle = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= middle.length) return;
    const next = [...middle];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    commit(next);
  };

  const renderLinkSelect = (
    value: string,
    onSelect: (next: { boardId: number | null; linkType: ContractStageLinkType | null }) => void
  ) => (
    <select value={value} onChange={(e) => onSelect(parseLinkSelect(e.target.value))}>
      <option value="">화면 연동 없음</option>
      <optgroup label="제품 메뉴">
        {STAGE_PRODUCT_LINK_OPTIONS.map((opt) => (
          <option key={opt.linkType} value={opt.linkType}>
            {opt.label}
          </option>
        ))}
      </optgroup>
      <optgroup label="품질 메뉴">
        {STAGE_QUALITY_LINK_OPTIONS.map((opt) => (
          <option key={opt.linkType} value={opt.linkType}>
            {opt.label}
          </option>
        ))}
      </optgroup>
      <optgroup label="문서">
        {STAGE_DOC_LINK_OPTIONS.map((opt) => (
          <option key={opt.linkType} value={opt.linkType}>
            {opt.label}
          </option>
        ))}
      </optgroup>
      <optgroup label="안전 메뉴">
        {STAGE_SAFETY_LINK_OPTIONS.map((opt) => (
          <option key={opt.linkType} value={opt.linkType}>
            {opt.label}
          </option>
        ))}
      </optgroup>
      <optgroup label="문서함(보드)">
        {boards.map((board) => (
          <option key={board.boardId} value={`BOARD:${board.boardId}`}>
            {board.boardName}
          </option>
        ))}
      </optgroup>
    </select>
  );

  return (
    <div className="contract-stage-editor">
      <div className="contract-stage-editor__head">
        <div>
          <strong>단계 구성</strong>
          <span>
            처음은 작성, 마지막은 완료로 고정됩니다. 가운데 단계만 편집하세요. 연동하면 해당 단계
            탭에 화면이 표시됩니다.
          </span>
        </div>
        <button type="button" onClick={onClose}>
          닫기
        </button>
      </div>

      <div className="contract-stage-editor__fixed">
        <span>1. 작성 (고정)</span>
      </div>

      {middle.map((stage, index) => (
        <div key={`middle-${index}`} className="contract-stage-editor__row">
          <span className="contract-stage-editor__order">{index + 2}</span>
          <input
            value={stage.stageName}
            maxLength={50}
            onChange={(e) => updateMiddle(index, { stageName: e.target.value })}
            placeholder="단계명"
          />
          {renderLinkSelect(linkSelectValue(stage), (next) => updateMiddle(index, next))}
          <div className="contract-stage-editor__moves">
            <button type="button" onClick={() => moveMiddle(index, -1)} disabled={index === 0}>
              ↑
            </button>
            <button
              type="button"
              onClick={() => moveMiddle(index, 1)}
              disabled={index === middle.length - 1}
            >
              ↓
            </button>
            <button type="button" className="is-danger" onClick={() => removeMiddle(index)}>
              삭제
            </button>
          </div>
        </div>
      ))}

      <button type="button" className="contract-stage-editor__add" onClick={addMiddle}>
        + 중간 단계 추가
      </button>

      <div className="contract-stage-editor__done">
        <span>{middle.length + 2}. 완료 (고정)</span>
        {renderLinkSelect(linkSelectValue(done), (next) => commit(middle, next))}
      </div>
    </div>
  );
}

export default ContractStageEditor;
